/**
 * `JSON.stringify` that never throws.
 *
 *  - Circular references → `"[Circular]"`
 *  - `bigint` → `"<n>n"`, `function` → `"[Function]"`, `symbol` → its string
 *  - Any residual failure (e.g. a pathologically deep object that overflows
 *    `JSON.stringify`'s own recursion) → `'"[Unserializable]"'`
 *
 * Shared by `logError()` (the Mixpanel-replay console payload) and the OTel
 * console bridge (`lib/otel/logger.ts`) so both telemetry surfaces are equally
 * crash-proof. Deliberately dependency-free — `lib/otel/logger.ts` is imported
 * during instrumentation bootstrap, before Sentry, so this must not pull in
 * `@sentry/nextjs` (which `logError.ts` does).
 */
export function safeStringify(value: unknown): string {
  const seen = new WeakSet<object>();
  try {
    return JSON.stringify(value, (_key, v) => {
      if (typeof v === "bigint") return `${v.toString()}n`;
      if (typeof v === "function") return "[Function]";
      if (typeof v === "symbol") return v.toString();
      if (typeof v === "object" && v !== null) {
        if (seen.has(v)) return "[Circular]";
        seen.add(v);
      }
      return v;
    });
  } catch {
    return '"[Unserializable]"';
  }
}
